import { apiService } from '@/services/api.service';
import type {
  AssignResult, Biome, BiomeInput, ChunkOp, CountriesList, Country, CountryInput, DeleteCountryResult, EraseResult,
  FillResult, GeoRegion, GeoRegionInput, ImportPreview, ImportReport, ImportVerify, Level, LevelInput,
  Marker, PaintResult, Possession, Rect, RosterPreview, RosterPushResult, RosterSyncState, UndoResult,
  Versioned, WorldsState,
} from './worlds.types';

// ============================================================
// Mondes (NationsGlory) : un appel par endpoint du module « worlds »
// ============================================================
// Tout passe par apiService.worldsRequest (JSON) ou getWorldsGrid (binaire RWG1). Les réponses
// gardent la forme { data?, error?, status? } : les écrans affichent `error` tel quel (le satellite
// répond toujours { message } en français).
// ============================================================

type Res<T> = Promise<{ data?: T; error?: string; status?: number }>;

const enc = encodeURIComponent;
const req = <T>(method: string, sub: string, body?: unknown): Res<T> => apiService.worldsRequest<T>(method, sub, body);

// ---- État ----
export const getState = (): Res<WorldsState> => req('GET', '/state');

// ---- Niveaux ----
export const listLevels = (): Res<Versioned<Level>> => req('GET', '/levels');
export const createLevel = (body: LevelInput): Res<Level> => req('POST', '/levels', body);
export const patchLevel = (name: string, body: LevelInput): Res<Level> => req('PATCH', `/levels/${enc(name)}`, body);
export const deleteLevel = (name: string): Res<null> => req('DELETE', `/levels/${enc(name)}`);

// ---- Grille ----
export const getGrid = (level: string, since: number) => apiService.getWorldsGrid(level, since);

export const paintChunks = (level: string, body: { countryId: string; rect?: Rect; cells?: number[] }): Res<PaintResult> =>
  req('POST', `/levels/${enc(level)}/chunks/paint`, body);
export const fillChunks = (level: string, body: { countryId: string; x: number; z: number }): Res<FillResult> =>
  req('POST', `/levels/${enc(level)}/chunks/fill`, body);
export const eraseChunks = (level: string, body: { countryId: string | null; rect?: Rect; cells?: number[] }): Res<EraseResult> =>
  req('POST', `/levels/${enc(level)}/chunks/erase`, body);
export const listOps = (level: string, limit = 50): Res<{ items: ChunkOp[] }> =>
  req('GET', `/levels/${enc(level)}/ops?limit=${limit}`);
export const undoOp = (level: string, opId: string): Res<UndoResult> =>
  req('POST', `/levels/${enc(level)}/ops/${enc(opId)}/undo`, {});

// ---- Pays ----
export const listCountries = (level?: string | null): Res<CountriesList> =>
  req('GET', `/countries${level ? `?level=${enc(level)}` : ''}`);
export const getCountry = (id: string, level?: string | null): Res<Country> =>
  req('GET', `/countries/${enc(id)}${level ? `?level=${enc(level)}` : ''}`);
export const createCountry = (body: CountryInput): Res<Country> => req('POST', '/countries', body);
export const patchCountry = (id: string, body: CountryInput): Res<Country> => req('PATCH', `/countries/${enc(id)}`, body);
export const deleteCountry = (id: string): Res<DeleteCountryResult> => req('DELETE', `/countries/${enc(id)}`);
/**
 * Affecte (ou retire, geoRegionId null) une géo-région à des pays. `fromGeoRegionId` : garde de
 * concurrence, seuls les pays encore dans cette région sont modifiés (les autres comptent dans
 * `skipped`). Absent ou null : pas de garde (le corps ne porte alors pas le champ).
 */
export const assignGeoRegion = (
  countryIds: string[], geoRegionId: string | null, fromGeoRegionId?: string | null,
): Res<AssignResult> =>
  req('POST', '/countries/geo-region', fromGeoRegionId != null
    ? { countryIds, geoRegionId, fromGeoRegionId }
    : { countryIds, geoRegionId });

// ---- Géo-régions ----
export const listGeoRegions = (): Res<Versioned<GeoRegion>> => req('GET', '/geo-regions');
export const createGeoRegion = (body: GeoRegionInput): Res<GeoRegion> => req('POST', '/geo-regions', body);
export const patchGeoRegion = (id: string, body: GeoRegionInput): Res<GeoRegion> => req('PATCH', `/geo-regions/${enc(id)}`, body);
export const deleteGeoRegion = (id: string): Res<{ deleted: boolean; detachedCountries: number }> =>
  req('DELETE', `/geo-regions/${enc(id)}`);

// ---- Biomes et marqueurs ----
export const listBiomes = (): Res<Versioned<Biome>> => req('GET', '/biomes');
export const createBiome = (body: BiomeInput): Res<Biome> => req('POST', '/biomes', body);
export const patchBiome = (name: string, body: BiomeInput): Res<Biome> => req('PATCH', `/biomes/${enc(name)}`, body);
export const deleteBiome = (name: string): Res<null> => req('DELETE', `/biomes/${enc(name)}`);

export const listMarkers = (level: string): Res<Versioned<Marker>> => req('GET', `/levels/${enc(level)}/markers`);
export const createMarker = (level: string, body: { x: number; z: number; biomeName: string }): Res<Marker> =>
  req('POST', `/levels/${enc(level)}/markers`, body);
export const patchMarker = (id: string, body: { x?: number; z?: number; biomeName?: string }): Res<Marker> =>
  req('PATCH', `/markers/${enc(id)}`, body);
export const deleteMarker = (id: string): Res<null> => req('DELETE', `/markers/${enc(id)}`);

// ---- Roster en jeu (NGCountryAPI) ----
export const getRoster = (): Res<RosterSyncState> => req('GET', '/roster');
export const getRosterPreview = (): Res<RosterPreview> => req('GET', '/roster/preview');
export const getPossession = (): Res<Possession> => req('GET', '/roster/possession');
export const syncRoster = (body: { force?: boolean; dryRun?: boolean }): Res<{ state: RosterSyncState; result: RosterPushResult | null }> =>
  req('POST', '/roster/sync', body);

// ---- Import NG Java ----
export const getImportPreview = (): Res<ImportPreview> => req('GET', '/import/ngjava');
export const runImport = (body: { mode: 'create' | 'replace'; confirm?: string }): Res<ImportReport> =>
  req('POST', '/import/ngjava', body);
export const verifyImport = (): Res<ImportVerify> => req('GET', '/import/verify');
